import type { ServerResponse } from 'http';
import { setCookie } from 'nookies';
import type { ParsedUrlQuery } from 'querystring';

import type { Country } from 'client-localisation/constants/countries';
import { type CurrencyCode, validCurrencies } from 'client-localisation/constants/currencies';
import { getCountry } from 'client-localisation/utils/countries';

export const CURRENCY_COOKIE = 'currency';
export const COUNTRY_CODE_COOKIE = 'country_code';
export const COUNTRY_NAME_COOKIE = 'country_name';
export const DELIVERY_ZONE_COOKIE = 'delivery_zone';

export const FORCE_CURRENCY_QUERY_PARAM = 'force_currency';

export const DEFAULT_LOCALE = getCountry('GB')!;

// Only the cookies are read, so this accepts any request that carries them.
export interface RequestWithCookies {
  cookies: Record<string, string>;
}

const getLocale = ({ cookies }: RequestWithCookies) => {
  const {
    [COUNTRY_CODE_COOKIE]: countryCode,
    [COUNTRY_NAME_COOKIE]: countryName,
    [CURRENCY_COOKIE]: currencyCode = '',
    [DELIVERY_ZONE_COOKIE]: deliveryZone,
  } = cookies || {};

  const currency = validCurrencies.includes(currencyCode) ? currencyCode : undefined;

  return { currency, countryCode, deliveryZone, countryName };
};
const hasLocaleSet = (req: RequestWithCookies) => {
  const { countryCode, countryName, currency, deliveryZone } = getLocale(req);

  return Boolean(currency && countryCode && deliveryZone && countryName);
};

const getLocaleCookieOptions = () => {
  const aYearFromNow = new Date();
  aYearFromNow.setFullYear(aYearFromNow.getFullYear() + 1);

  return { expires: aYearFromNow, path: '/' };
};

const setLocale = (country: Country, res: ServerResponse) => {
  const cookieOptions = getLocaleCookieOptions();

  setCookie({ res }, CURRENCY_COOKIE, country?.currency, cookieOptions);
  setCookie({ res }, COUNTRY_CODE_COOKIE, country?.countryCode, cookieOptions);
  setCookie({ res }, COUNTRY_NAME_COOKIE, country?.name, cookieOptions);
  setCookie({ res }, DELIVERY_ZONE_COOKIE, country?.deliveryZoneCode, cookieOptions);
};

export const sanitiseForceCurrencyParam = (
  query: ParsedUrlQuery = {},
  userSetCurrency: CurrencyCode | undefined,
): CurrencyCode | null => {
  const forceCurrencyParam = query[FORCE_CURRENCY_QUERY_PARAM];

  if (forceCurrencyParam) {
    const currency = (
      Array.isArray(forceCurrencyParam) ? forceCurrencyParam[0] : forceCurrencyParam
    ).toUpperCase();

    const isValidCurrencyCode = validCurrencies.includes(currency);

    if (isValidCurrencyCode) {
      return currency as CurrencyCode;
    }
  }

  if (userSetCurrency && userSetCurrency !== DEFAULT_LOCALE.currency) {
    return userSetCurrency;
  }

  return null;
};

interface SetLocaleCookiesArguments {
  query?: ParsedUrlQuery;
  req: RequestWithCookies;
  res: ServerResponse;
}

export const setLocaleCookies = ({ query = {}, req, res }: SetLocaleCookiesArguments) => {
  const hasLocaleSetByUser = hasLocaleSet(req);

  if (!hasLocaleSetByUser) {
    setLocale(DEFAULT_LOCALE, res);
  }

  const userSetLocaleCurrency = getLocale(req).currency as CurrencyCode;

  const forceCurrencyParam = sanitiseForceCurrencyParam(query, userSetLocaleCurrency);

  if (forceCurrencyParam) {
    setCookie({ res }, CURRENCY_COOKIE, forceCurrencyParam, getLocaleCookieOptions());
  }
};
